/**
 * 端点层 —— 页面唯一的数据入口。
 *
 * 页面不允许直接调用 `apiFetch`，也不允许直接 import mocks：
 * mock/真实的切换必须**收敛在这一处**，否则「上线时忘了摘掉某个 mock」是必然发生的事故。
 *
 * 默认走**真实后端**。`VITE_USE_MOCKS=true` 才回落到 src/mocks（离线开发 / QA 手工验收五态）。
 * 默认值方向很重要：默认 mock 会让「忘了配环境变量」表现为「页面好像能用」，
 * 默认真实则表现为「连不上后端」——后者立刻可见，前者能瞒到上线。
 */
import { apiFetch, ApiError, CONTRACT_NOT_READY_CODE, invalidateCsrfToken } from './client';
import type {
  AdminUser,
  ApiKey,
  CodeBatch,
  Customer,
  CsrfData,
  EntitlementValue,
  InventoryRow,
  IpBlacklistEntry,
  License,
  LicenseDetail,
  ListLicensesQuery,
  ListProductsQuery,
  LoginRequest,
  Order,
  Paged,
  Plan,
  Product,
  ProductIntegration,
  ProductInventory,
  ProductVersion,
  ReadinessData,
  RedemptionRequest,
  RevokeResult,
  Session,
  StatsOverview,
  EntitlementKey,
  CreateEntitlementKeyRequest,
  UpdateEntitlementKeyRequest,
  CreateLicenseRequest,
  CreateApiKeyResult,
  CustomerInput,
  CreateOrderRequest,
} from './types';
import { DEFAULT_INVENTORY_LOW_WATERMARK } from './types';
import {
  mockCreateAdminUser,
  mockCreateCodeBatch,
  mockDeleteAdminUser,
  mockCreateEntitlementKey,
  mockCreateLicense,
  mockCreatePlan,
  mockCreateProduct,
  mockCustomers,
  mockCreateCustomer,
  mockUpdateCustomer,
  mockCreateOrder,
  mockDeactivateActivation,
  mockDeleteEntitlementKey,
  mockDeleteProduct,
  mockBulkVoidLicenseCodes,
  mockGetCodeBatch,
  mockExportCodeBatchCsv,
  mockGetLicense,
  mockGetPlan,
  mockGetProduct,
  mockGetProductIntegration,
  mockInventory,
  mockListActivations,
  mockListIpBlacklist,
  mockUnblockIp,
  mockListAdminUsers,
  mockListApiKeys,
  mockListAuditEvents,
  mockListCodeBatches,
  mockDeleteAuditEvent,
  mockDeleteAuditEvents,
  mockListEntitlementKeys,
  mockListLicenseActivations,
  mockListLicenseCodes,
  mockListOrders,
  mockListProductVersions,
  mockListRedemptionRequests,
  mockListSessions,
  mockListSigningKeys,
  mockCompromiseSigningKey,
  mockCreateApiKey,
  mockRetireSigningKey,
  mockRevokeAllSessions,
  mockRevokeApiKey,
  mockRevokeSession,
  mockRotateSigningKey,
  mockUpdateAdminUser,
  mockVoidLicenseCode,
  mockLicenses,
  mockLogin,
  mockLogout,
  mockMe,
  mockPlans,
  mockProducts,
  mockReadiness,
  mockRenewLicense,
  mockResumeLicense,
  mockRevokeLicense,
  mockStatsOverview,
  mockSuspendLicense,
  mockUpdateEntitlementKey,
  mockUpdateLicense,
  mockUpdatePlan,
  mockUpdateProduct,
} from '../mocks/handlers';

export const USE_MOCKS = (import.meta.env.VITE_USE_MOCKS ?? 'false') === 'true';

/* ────────────────────────── 认证（P-01） ────────────────────────── */

/**
 * 取 CSRF token。登录表单挂载时预取一次，避免用户点「登录」时多等一个 RTT。
 * 变更请求路径上的自动获取由 client.ensureCsrfToken 负责，这里只是提前热身。
 */
export function getCsrfToken(): Promise<CsrfData> {
  if (USE_MOCKS) return Promise.resolve({ csrf_token: 'mock-csrf-token' });
  return apiFetch<CsrfData>('/admin/auth/csrf', { skipAuthRedirect: true });
}

export function login(body: LoginRequest): Promise<AdminUser> {
  if (USE_MOCKS) return mockLogin(body);
  return apiFetch<AdminUser>('/admin/auth/login', {
    method: 'POST',
    body,
    // 登录失败的 401 由登录页自己显示文案，不能触发「跳登录页」
    skipAuthRedirect: true,
  });
}

export function logout(): Promise<void> {
  if (USE_MOCKS) return mockLogout();
  return apiFetch<void>('/admin/auth/logout', { method: 'POST' }).finally(() => {
    // 会话没了，旧 CSRF token 也随之作废
    invalidateCsrfToken();
  });
}

/** 启动时用它判断「有没有活着的会话」。401 是正常分支，不是错误 */
export function getCurrentAdmin(): Promise<AdminUser> {
  if (USE_MOCKS) return mockMe();
  return apiFetch<AdminUser>('/admin/auth/me', { skipAuthRedirect: true });
}

/* ────────────────────────── 统计与健康 ────────────────────────── */

export function getStatsOverview(productId?: string): Promise<StatsOverview> {
  if (USE_MOCKS) return mockStatsOverview();
  return apiFetch<StatsOverview>('/admin/stats/overview', {
    query: { product_id: productId },
  });
}

/** 就绪探针在 /api/v1 之外没有单独前缀，仍位于同一 base 下（openapi servers 已含 /api/v1） */
export function getReadiness(): Promise<ReadinessData> {
  if (USE_MOCKS) return mockReadiness();
  return apiFetch<ReadinessData>('/health/ready');
}

/* ────────────────────────── 目录与授权 ────────────────────────── */

/** 产品表规模 < 1000，§6.4 规定一次加载完不分页；limit 取契约上限 200 */
const CATALOG_PAGE_LIMIT = 200;

export function listProducts(query: ListProductsQuery = {}): Promise<Paged<Product>> {
  if (USE_MOCKS) return mockProducts();
  return apiFetch<Paged<Product>>('/admin/products', {
    query: { q: query.q, page: query.page ?? 1, limit: query.limit ?? CATALOG_PAGE_LIMIT },
  });
}

/** 产品详情（openapi getProduct，P-04） */
export function getProduct(productId: string): Promise<Product> {
  if (USE_MOCKS) return mockGetProduct(productId);
  return apiFetch<Product>(`/admin/products/${encodeURIComponent(productId)}`);
}

/** 下游 SDK 接入信息（产品 UUID / 根公钥 / 基址），对应客户端 .env 的 LH_* */
export function getProductIntegration(productId: string): Promise<ProductIntegration> {
  if (USE_MOCKS) return mockGetProductIntegration(productId);
  return apiFetch<ProductIntegration>(`/admin/products/${encodeURIComponent(productId)}/integration`);
}

/** 产品版本列表（openapi listProductVersions，P-04） */
export function listProductVersions(productId: string, query: { page?: number; limit?: number } = {}): Promise<Paged<ProductVersion>> {
  if (USE_MOCKS) return mockListProductVersions(productId);
  return apiFetch<Paged<ProductVersion>>(`/admin/products/${encodeURIComponent(productId)}/versions`, {
    query: { page: query.page ?? 1, limit: query.limit ?? 50 },
  });
}

/** 软删除产品（归档，T2，owner）：存在未撤销授权时拒绝 9005。 */
export function deleteProduct(productId: string): Promise<void> {
  if (USE_MOCKS) return mockDeleteProduct(productId);
  return apiFetch<void>(`/admin/products/${encodeURIComponent(productId)}`, { method: 'DELETE' });
}

export function createProduct(data: { slug: string; name: string; description?: string | null }): Promise<Product> {
  if (USE_MOCKS) return mockCreateProduct(data);
  return apiFetch<Product>('/admin/products', { method: 'POST', body: data });
}

export function updateProduct(id: string, data: { name?: string; description?: string | null; is_active?: boolean }): Promise<Product> {
  if (USE_MOCKS) return mockUpdateProduct(id, data);
  return apiFetch<Product>(`/admin/products/${encodeURIComponent(id)}`, { method: 'PATCH', body: data });
}

export function listPlans(productId?: string): Promise<Paged<Plan>> {
  if (USE_MOCKS) return mockPlans(productId);
  return apiFetch<Paged<Plan>>('/admin/plans', {
    query: { product_id: productId, limit: CATALOG_PAGE_LIMIT },
  });
}

export function createPlan(productId: string, data: {
  code: string;
  name: string;
  max_devices: number;
  duration_days?: number | null;
  offline_grace_seconds?: number;
  version_constraint?: string | null;
  entitlements?: Record<string, EntitlementValue>;
}): Promise<Plan> {
  if (USE_MOCKS) return mockCreatePlan(productId, data);
  return apiFetch<Plan>(`/admin/products/${encodeURIComponent(productId)}/plans`, { method: 'POST', body: data });
}

export function updatePlan(planId: string, data: {
  name?: string;
  max_devices?: number;
  duration_days?: number | null;
  offline_grace_seconds?: number;
  version_constraint?: string | null;
  is_active?: boolean;
  entitlements?: Record<string, EntitlementValue>;
}): Promise<Plan> {
  if (USE_MOCKS) return mockUpdatePlan(planId, data);
  return apiFetch<Plan>(`/admin/plans/${encodeURIComponent(planId)}`, { method: 'PATCH', body: data });
}

export function getPlan(planId: string): Promise<Plan> {
  if (USE_MOCKS) return mockGetPlan(planId);
  return apiFetch<Plan>(`/admin/plans/${encodeURIComponent(planId)}`);
}

export function listCustomers(query: { q?: string; page?: number; limit?: number } = {}): Promise<Paged<Customer>> {
  if (USE_MOCKS) return mockCustomers({ q: query.q, page: query.page ?? 1, limit: query.limit ?? CATALOG_PAGE_LIMIT });
  return apiFetch<Paged<Customer>>('/admin/customers', {
    query: { q: query.q, page: query.page ?? 1, limit: query.limit ?? CATALOG_PAGE_LIMIT },
  });
}

/** 登记客户（admin-crm POST /admin/customers）。 */
export function createCustomer(input: CustomerInput): Promise<Customer> {
  if (USE_MOCKS) return mockCreateCustomer(input);
  return apiFetch<Customer>('/admin/customers', { method: 'POST', body: input });
}

/** 编辑客户（admin-crm PATCH /admin/customers/:id）。 */
export function updateCustomer(customerId: string, input: CustomerInput): Promise<Customer> {
  if (USE_MOCKS) return mockUpdateCustomer(customerId, input);
  return apiFetch<Customer>(`/admin/customers/${encodeURIComponent(customerId)}`, { method: 'PATCH', body: input });
}

/** 按客户加载订单（后端统一走 /admin/orders?customer_id=，openapi listOrders）。 */
export function listOrders(customerId: string): Promise<Paged<Order>> {
  if (USE_MOCKS) return mockListOrders(customerId);
  return apiFetch<Paged<Order>>('/admin/orders', {
    query: { customer_id: customerId, limit: 200 },
  });
}

/** 登记订单（admin-crm POST /admin/orders，后台手动录入，source=manual）。 */
export function createOrder(input: CreateOrderRequest): Promise<Order> {
  if (USE_MOCKS) return mockCreateOrder(input);
  return apiFetch<Order>('/admin/orders', { method: 'POST', body: input });
}

/**
 * 授权列表（OD-016）。
 * 分页是**页式**：`page` / `limit` 入参，`PageMeta{total, page, limit, hasMore}` 出参。
 * 契约（openapi #/components/parameters/Page + Limit + schemas/PageMeta）与
 * docs/03-UIUX.md §6.4 现已一致；此前 §6.4 写的游标分页是文档侧笔误，已随本次修正。
 */
export function listLicenses(query: ListLicensesQuery = {}): Promise<Paged<License>> {
  if (USE_MOCKS) return mockLicenses(query);
  return apiFetch<Paged<License>>('/admin/licenses', {
    query: {
      product_id: query.product_id,
      status: query.status,
      plan_id: query.plan_id,
      customer_id: query.customer_id,
      expires_before: query.expires_before,
      q: query.q,
      sort: query.sort,
      order: query.order,
      page: query.page ?? 1,
      limit: query.limit ?? CATALOG_PAGE_LIMIT,
    },
  });
}

/**
 * 管理员手动创建授权（P-12，不消耗库存码）。后端返回 201 + License（LicenseResponse）。
 * 用于补发、测试、赠送；同样写入 plan_snapshot 并记审计。
 */
export function createLicense(body: CreateLicenseRequest): Promise<License> {
  if (USE_MOCKS) return mockCreateLicense(body);
  return apiFetch<License>('/admin/licenses', {
    method: 'POST',
    body: {
      product_id: body.product_id,
      plan_id: body.plan_id,
      customer_id: body.customer_id ?? null,
      expires_at: body.expires_at ?? null,
      note: body.note ?? null,
    },
  });
}

/* ────────────────────────── 授权详情与生命周期（P-11） ────────────────────────── */
/**
 * 详情页的唯一数据入口。后端返回 LicenseView + activations + customer（LicenseDetailResponse）。
 * 注意前端 `License` 类型本身不含 activations/customer，这俩只在这里出现，页面不得假设列表项带它们。
 */
export function getLicense(licenseId: string): Promise<LicenseDetail> {
  if (USE_MOCKS) return mockGetLicense(licenseId);
  return apiFetch<LicenseDetail>(`/admin/licenses/${encodeURIComponent(licenseId)}`);
}

/** 只允许改 note 与 customer_id（契约 UpdateLicenseRequest，additionalProperties:false） */
export function updateLicense(
  licenseId: string,
  body: { note?: string | null; customer_id?: string | null },
): Promise<License> {
  if (USE_MOCKS) return mockUpdateLicense(licenseId, body);
  return apiFetch<License>(`/admin/licenses/${encodeURIComponent(licenseId)}`, {
    method: 'PATCH',
    body,
  });
}

/** 撤销（终态）。reason 必填，写入审计；返回预期最迟离线失效时刻（PRD P0-4） */
export function revokeLicense(licenseId: string, reason: string): Promise<RevokeResult> {
  if (USE_MOCKS) return mockRevokeLicense(licenseId, reason);
  return apiFetch<RevokeResult>(`/admin/licenses/${encodeURIComponent(licenseId)}/revoke`, {
    method: 'POST',
    body: { reason },
  });
}

/** 暂停（可恢复）。reason 必填 */
export function suspendLicense(licenseId: string, reason: string): Promise<RevokeResult> {
  if (USE_MOCKS) return mockSuspendLicense(licenseId, reason);
  return apiFetch<RevokeResult>(`/admin/licenses/${encodeURIComponent(licenseId)}/suspend`, {
    method: 'POST',
    body: { reason },
  });
}

/** 恢复被暂停的授权（suspended → active），无请求体 */
export function resumeLicense(licenseId: string): Promise<License> {
  if (USE_MOCKS) return mockResumeLicense(licenseId);
  return apiFetch<License>(`/admin/licenses/${encodeURIComponent(licenseId)}/resume`, {
    method: 'POST',
  });
}

/** 续期：延长（active→active）或复活（expired→active）。refresh_plan_snapshot 默认 false */
export function renewLicense(
  licenseId: string,
  body: { expires_at: number | null; refresh_plan_snapshot?: boolean; reason?: string | null },
): Promise<License> {
  if (USE_MOCKS) return mockRenewLicense(licenseId, body);
  return apiFetch<License>(`/admin/licenses/${encodeURIComponent(licenseId)}/renew`, {
    method: 'POST',
    body,
  });
}

/** 该授权下的设备激活记录（默认只返回活跃设备） */
export function listLicenseActivations(
  licenseId: string,
  opts: { includeDeactivated?: boolean } = {},
): Promise<Activation[]> {
  if (USE_MOCKS) return mockListLicenseActivations(licenseId, opts.includeDeactivated ?? false);
  return apiFetch<Activation[]>(`/admin/licenses/${encodeURIComponent(licenseId)}/activations`, {
    query: { include_deactivated: opts.includeDeactivated ?? false },
  });
}

/** 管理员强制解绑设备（与客户端自助解绑同事务纪律），reason 必填 */
export function deactivateActivation(activationId: string, reason: string): Promise<Activation> {
  if (USE_MOCKS) return mockDeactivateActivation(activationId, reason);
  return apiFetch<Activation>(`/admin/activations/${encodeURIComponent(activationId)}/deactivate`, {
    method: 'POST',
    body: { reason },
  });
}

/* ────────────────────────── 库存水位（OD-017） ────────────────────────── */

/**
 * 仪表盘一次最多为多少个产品拉库存。
 * 这是个扇出请求，不设上限就等于把 N+1 写进首屏（失效模式 #5 性能盲区）。
 * 自托管场景产品数通常个位数；超出部分不展示，并在 UI 上说明只显示前 N 个。
 */
export const INVENTORY_FANOUT_LIMIT = 12;

function notReady(path: string): ApiError {
  return new ApiError({
    code: CONTRACT_NOT_READY_CODE,
    message: `库存水位端点 ${path} 尚未就绪（openapi.yaml 未定义该路径 / 后端未实现）。`,
    httpStatus: 0,
  });
}

/** 后端形状未进契约，必须逐字段验，不能 as 一下就当真（失效模式 #3） */
function parseInventory(raw: unknown, path: string): ProductInventory {
  if (typeof raw !== 'object' || raw === null) throw notReady(path);
  const record = raw as Record<string, unknown>;
  const num = (key: string): number => {
    const value = record[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) throw notReady(path);
    return value;
  };
  const productId = record['product_id'];
  if (typeof productId !== 'string' || productId.length === 0) throw notReady(path);

  const name = record['product_name'];
  const threshold = record['low_watermark'];
  return {
    product_id: productId,
    ...(typeof name === 'string' && name.length > 0 ? { product_name: name } : {}),
    available: num('available'),
    exported: num('exported'),
    redeemed: num('redeemed'),
    void: num('void'),
    ...(typeof threshold === 'number' && Number.isFinite(threshold)
      ? { low_watermark: threshold }
      : {}),
  };
}

export async function getProductInventory(productId: string): Promise<ProductInventory> {
  const path = `/admin/products/${encodeURIComponent(productId)}/inventory`;
  const raw = await apiFetch<unknown>(path);
  return parseInventory(raw, path);
}

/**
 * P-02 库存水位。逐产品调用 OD-017 端点后聚合。
 *
 * 失败策略刻意分成两档：
 * - 端点整体未就绪（首个产品就 404 / 形状不符）→ 抛 CONTRACT_NOT_READY，UI 给中性「未就绪」说明；
 * - 个别产品失败 → 跳过该行，其余照常展示。让一个产品的库存查询失败拖垮整块面板是不必要的。
 */
export async function listInventoryWatermarks(): Promise<InventoryRow[]> {
  if (USE_MOCKS) return mockInventory();

  const products = await listProducts();
  const targets = products.items
    .filter((product) => product.is_active)
    .slice(0, INVENTORY_FANOUT_LIMIT);
  if (targets.length === 0) return [];

  const settled = await Promise.allSettled(
    targets.map(async (product) => {
      const inventory = await getProductInventory(product.id);
      const row: InventoryRow = {
        product_id: product.id,
        product_name: inventory.product_name ?? product.name,
        available: inventory.available,
        exported: inventory.exported,
        redeemed: inventory.redeemed,
        void: inventory.void,
        low_watermark: inventory.low_watermark ?? DEFAULT_INVENTORY_LOW_WATERMARK,
      };
      return row;
    }),
  );

  const rows: InventoryRow[] = [];
  for (const result of settled) {
    if (result.status === 'fulfilled') rows.push(result.value);
  }

  if (rows.length === 0) {
    const firstRejection = settled.find((result) => result.status === 'rejected');
    if (firstRejection?.status === 'rejected') {
      throw firstRejection.reason instanceof Error
        ? firstRejection.reason
        : notReady('/admin/products/{product_id}/inventory');
    }
  }

  return rows;
}

export type { InventoryRow };

/* ────────────────────────── 发行（P-06/P-07/P-08） ────────────────────────── */

export interface LicenseCode {
  id: string;
  product_id: string;
  plan_id: string;
  batch_id: string;
  code_mask: string;
  status: 'available' | 'exported' | 'redeemed' | 'void';
  redeemed_at: number | null;
  void_reason: string | null;
  created_at: number;
}

export function listCodeBatches(productId?: string): Promise<Paged<CodeBatch>> {
  if (USE_MOCKS) return mockListCodeBatches(productId);
  return apiFetch<Paged<CodeBatch>>('/admin/code-batches', {
    query: { product_id: productId, limit: 200 },
  });
}

/** 批次详情（openapi getCodeBatch）：批次元信息 + stats（P-08）。 */
export function getCodeBatch(batchId: string): Promise<CodeBatch> {
  if (USE_MOCKS) return mockGetCodeBatch(batchId);
  return apiFetch<CodeBatch>(`/admin/code-batches/${encodeURIComponent(batchId)}`);
}

export function createCodeBatch(
  productId: string,
  planId: string,
  quantity: number,
  opts: { note?: string; idempotencyKey: string },
): Promise<CodeBatch> {
  if (USE_MOCKS) return mockCreateCodeBatch(productId, planId, quantity, opts);
  return apiFetch<CodeBatch>(`/admin/products/${encodeURIComponent(productId)}/code-batches`, {
    method: 'POST',
    body: { plan_id: planId, quantity, note: opts.note ?? null },
    headers: { 'Idempotency-Key': opts.idempotencyKey },
  });
}

export function listLicenseCodes(query: {
  product_id?: string;
  batch_id?: string;
  status?: string;
  page?: number;
  limit?: number;
} = {}): Promise<Paged<LicenseCode>> {
  if (USE_MOCKS) return mockListLicenseCodes(query);
  return apiFetch<Paged<LicenseCode>>('/admin/license-codes', { query });
}

/** 作废单个授权码（T1，reason 必填，写入审计）。 */
export function voidLicenseCode(codeId: string, reason: string): Promise<LicenseCode> {
  if (USE_MOCKS) return mockVoidLicenseCode(codeId, reason);
  return apiFetch<LicenseCode>(`/admin/license-codes/${encodeURIComponent(codeId)}/void`, {
    method: 'POST',
    body: { reason },
  });
}

/** 批量作废（T2，原子）：任一条已兑换则整体拒绝；ids ≤ 200。 */
export function bulkVoidLicenseCodes(ids: string[], reason: string): Promise<{ voided: number }> {
  if (USE_MOCKS) return mockBulkVoidLicenseCodes(ids, reason);
  return apiFetch<{ voided: number }>('/admin/license-codes/bulk-void', {
    method: 'POST',
    body: { ids, reason },
  });
}

/**
 * 一次性导出批次 CSV。后端返回 text/csv（HTTP 200 + Content-Disposition）。
 * 由于它不走 JSON 信封，这里直接 fetch 并返回原始文本；由调用方触发浏览器下载。
 * 注意：路径为 /admin/code-batches/:batch_id/export（顶层，不带 product scope，见 openapi）。
 */
export async function exportCodeBatchCsv(batchId: string): Promise<string> {
  if (USE_MOCKS) return mockExportCodeBatchCsv(batchId);
  const { API_BASE, ensureCsrfToken } = await import('./client');
  const csrf = await ensureCsrfToken();
  const url = `${API_BASE}/admin/code-batches/${encodeURIComponent(batchId)}/export`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'text/csv', 'X-CSRF-Token': csrf ?? '' },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`导出失败（HTTP ${response.status}）`);
  }
  const text = await response.text();
  // 后端可能用 JSON 信封包裹（{code:0,data:"..."}）或返回裸 CSV
  try {
    const envelope = JSON.parse(text) as { code?: number; message?: string; data?: unknown };
    if (typeof envelope.code === 'number' && envelope.code !== 0) {
      throw new Error(envelope.message ?? '导出失败');
    }
    if (typeof envelope.data === 'string') return envelope.data;
  } catch (e) {
    if (e instanceof SyntaxError) return text; // 裸 CSV
    throw e;
  }
  return text;
}

/* ────────────────────────── 安全（P-15/P-16/P-17/P-23） ────────────────────────── */

export interface SigningKey {
  id: string;
  product_id: string;
  /** 契约字段（openapi SigningKey.key_id），与后端 view() 逐字对齐 */
  key_id: string;
  algorithm: string;
  public_key_b64u: string;
  /** 该密钥是否有可用私钥（KEK 加密落库）；响应不暴露私钥密文本身 */
  has_private_key: boolean;
  role: 'root' | 'leaf';
  status: 'active' | 'retiring' | 'retired' | 'compromised';
  not_before: number;
  not_after: number | null;
  retired_at: number | null;
  created_at: number;
}

/** 轮换响应（openapi RotateKeyResultResponse.data） */
export interface RotateKeyResult {
  new_key: SigningKey;
  retiring_key: SigningKey | null;
  /** 恒为 true：根私钥离线，公钥集重签需人工介入 */
  requires_root_signature: true;
}

/** 应急吊销响应（openapi CompromiseResultResponse.data） */
export interface CompromiseKeyResult {
  key: SigningKey;
  estimated_affected_licenses: number;
  requires_root_signature: true;
}




export function listSigningKeys(productId?: string): Promise<SigningKey[]> {
  if (USE_MOCKS) return mockListSigningKeys(productId);
  // 后端 /admin/signing-keys 返回数组（非分页），只接受 product_id 查询参数
  return apiFetch<SigningKey[]>('/admin/signing-keys', { query: { product_id: productId } });
}

/** 轮换叶密钥（T3，owner）。旧 active → retiring（保留期 90 天），新叶 → active。 */
export function rotateSigningKey(
  productId: string,
  body: { reason: string; retiring_until?: number | null },
): Promise<RotateKeyResult> {
  if (USE_MOCKS) return mockRotateSigningKey(productId, body);
  return apiFetch<RotateKeyResult>(`/admin/products/${encodeURIComponent(productId)}/signing-keys/rotate`, {
    method: 'POST',
    body,
  });
}

/** 应急吊销（T3，owner）。不走 retiring 缓冲，立即移出公钥集。 */
export function compromiseSigningKey(signingKeyId: string, reason: string): Promise<CompromiseKeyResult> {
  if (USE_MOCKS) return mockCompromiseSigningKey(signingKeyId, reason);
  return apiFetch<CompromiseKeyResult>(`/admin/signing-keys/${encodeURIComponent(signingKeyId)}/compromise`, {
    method: 'POST',
    body: { reason },
  });
}

/** 下线 retiring 密钥（T3，owner）。保留期未满返回 9009。 */
export function retireSigningKey(signingKeyId: string): Promise<SigningKey> {
  if (USE_MOCKS) return mockRetireSigningKey(signingKeyId);
  return apiFetch<SigningKey>(`/admin/signing-keys/${encodeURIComponent(signingKeyId)}/retire`, { method: 'POST' });
}

export function listAdminUsers(): Promise<AdminUser[]> {
  if (USE_MOCKS) return mockListAdminUsers();
  return apiFetch<AdminUser[]>('/admin/admin-users', { query: { limit: 200 } });
}

export function createAdminUser(data: { email: string; name: string; password: string; role: string }): Promise<AdminUser> {
  if (USE_MOCKS) return mockCreateAdminUser(data);
  return apiFetch<AdminUser>('/admin/admin-users', { method: 'POST', body: data });
}

/** 更新管理员（owner）：改角色 / 停用启用 / 重置密码。停用最后一个 owner 后端返回 9010。 */
export function updateAdminUser(
  adminUserId: string,
  data: { name?: string; role?: string; is_active?: boolean; reset_password?: string },
): Promise<AdminUser> {
  if (USE_MOCKS) return mockUpdateAdminUser(adminUserId, data);
  return apiFetch<AdminUser>(`/admin/admin-users/${encodeURIComponent(adminUserId)}`, { method: 'PATCH', body: data });
}

/** 删除管理员（owner）：软删除，会停用账号并撤销全部会话。最后一个 owner 不可删除。 */
export function deleteAdminUser(adminUserId: string): Promise<void> {
  if (USE_MOCKS) return mockDeleteAdminUser(adminUserId);
  return apiFetch<void>(`/admin/admin-users/${encodeURIComponent(adminUserId)}`, { method: 'DELETE' });
}

export function listApiKeys(): Promise<ApiKey[]> {
  if (USE_MOCKS) return mockListApiKeys();
  return apiFetch<ApiKey[]>('/admin/api-keys', { query: { limit: 200 } });
}

/** 创建 API Key（owner）：明文只在响应中出现一次，服务端不保存明文。 */
export function createApiKey(data: { name: string; role: string; expires_at?: number | null; scopes?: string[] }): Promise<CreateApiKeyResult> {
  if (USE_MOCKS) return mockCreateApiKey(data);
  return apiFetch<CreateApiKeyResult>('/admin/api-keys', { method: 'POST', body: data });
}

/** 撤销 API Key（owner，T2）。撤销后密钥立即失效。 */
export function revokeApiKey(apiKeyId: string): Promise<void> {
  if (USE_MOCKS) return mockRevokeApiKey(apiKeyId);
  return apiFetch<void>(`/admin/api-keys/${encodeURIComponent(apiKeyId)}`, { method: 'DELETE' });
}

export function listSessions(): Promise<Session[]> {
  if (USE_MOCKS) return mockListSessions();
  return apiFetch<Session[]>('/admin/sessions');
}

/** 踢下线（T1）：撤销指定会话。当前会话不可踢自己。 */
export function revokeSession(sessionId: string): Promise<void> {
  if (USE_MOCKS) return mockRevokeSession(sessionId);
  return apiFetch<void>(`/admin/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}

/** 全部登出（P-23）：撤销除当前会话外的全部会话。 */
export function revokeAllSessions(): Promise<{ revoked: number }> {
  if (USE_MOCKS) return mockRevokeAllSessions();
  return apiFetch<{ revoked: number }>('/admin/sessions', { method: 'DELETE' });
}

/* ────────────────────────── 审计与系统（P-18/P-19/P-22） ────────────────────────── */

export interface AuditEvent {
  id: string;
  actor_type: string;
  actor_id: string | null;
  product_id: string | null;
  license_id: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  reason: string | null;
  detail: unknown;
  ip: string | null;
  user_agent: string | null;
  /** 事件结果（P-18 失败标记）：failure 行首红色左标记 + octagon-x */
  result: 'success' | 'failure';
  prev_hash: string;
  entry_hash: string;
  created_at: number;
}


export function listAuditEvents(query: { page?: number; limit?: number; action?: string } = {}): Promise<Paged<AuditEvent>> {
  if (USE_MOCKS) return mockListAuditEvents(query);
  return apiFetch<Paged<AuditEvent>>('/admin/audit-events', { query });
}

/** 单条删除审计事件（owner）。uuid 不存在返回 404。 */
export function deleteAuditEvent(id: string): Promise<void> {
  if (USE_MOCKS) return mockDeleteAuditEvent(id);
  return apiFetch<void>(`/admin/audit-events/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** 批量删除审计事件（owner）。返回实际删除条数。 */
export function deleteAuditEvents(ids: string[]): Promise<{ deleted: number }> {
  if (USE_MOCKS) return mockDeleteAuditEvents(ids);
  return apiFetch<{ deleted: number }>('/admin/audit-events/batch-delete', { method: 'POST', body: { ids } });
}

/* ────────────────────────── 兑换请求记录（P-22） ────────────────────────── */

export type { RedemptionRequest };

/**
 * 兑换请求记录列表（只读、倒序、分页）。后端无此端点时（Sprint 尚未实现）
 * 回落到 mock；真实后端路径为 `GET /admin/redemptions`（契约待补入 openapi.yaml）。
 */
export function listRedemptionRequests(query: { page?: number; limit?: number } = {}): Promise<Paged<RedemptionRequest>> {
  if (USE_MOCKS) return mockListRedemptionRequests(query);
  return apiFetch<Paged<RedemptionRequest>>('/admin/redemptions', { query });
}

/* ────────────────────────── 功能开关注册表（P-21） ────────────────────────── */

/**
 * 功能开关键注册表（按产品维度）。openapi: GET /admin/products/{product_id}/entitlement-keys。
 * 返回**纯数组**（非分页）——单产品的开关键规模很小，无需游标/偏移。
 */
export function listEntitlementKeys(productId?: string): Promise<EntitlementKey[]> {
  if (USE_MOCKS) return mockListEntitlementKeys(productId);
  return apiFetch<EntitlementKey[]>('/admin/entitlement-keys', { query: { product_id: productId } });
}

/** 注册功能开关键。openapi: POST .../entitlement-keys */
export function createEntitlementKey(productId: string, body: CreateEntitlementKeyRequest): Promise<EntitlementKey> {
  if (USE_MOCKS) return mockCreateEntitlementKey(productId, body);
  return apiFetch<EntitlementKey>(`/admin/products/${encodeURIComponent(productId)}/entitlement-keys`, {
    method: 'POST',
    body,
  });
}

/** 修改功能开关键（key 不可改）。openapi: PATCH /admin/entitlement-keys/{id} */
export function updateEntitlementKey(id: string, body: UpdateEntitlementKeyRequest): Promise<EntitlementKey> {
  if (USE_MOCKS) return mockUpdateEntitlementKey(id, body);
  return apiFetch<EntitlementKey>(`/admin/entitlement-keys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

/** 删除功能开关键。仍被套餐引用时后端返回 9005 has_dependents。openapi: DELETE /admin/entitlement-keys/{id} */
export function deleteEntitlementKey(id: string): Promise<void> {
  if (USE_MOCKS) return mockDeleteEntitlementKey(id);
  return apiFetch<void>(`/admin/entitlement-keys/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ────────────────────────── 激活设备（P-13） ────────────────────────── */

export interface Activation {
  id: string;
  license_id: string;
  product_id: string;
  device_hash: string;
  device_name: string | null;
  status: 'active' | 'deactivated';
  activated_at: number;
  last_seen_at: number | null;
  deactivated_at: number | null;
  deactivated_by: string | null;
}

export function listActivations(query: { page?: number; limit?: number; status?: string; license_id?: string } = {}): Promise<Paged<Activation>> {
  if (USE_MOCKS) return mockListActivations(query);
  return apiFetch<Paged<Activation>>('/admin/activations', { query });
}

/* ────────────────────────── 安全：IP 黑名单（P-23 衍生） ────────────────────────── */

/**
 * 列出当前生效（含未过期）的 IP 封禁。owner 专属（后端 @Roles('owner')）。
 * 这是自动封禁（限流/登录爆破触发）的「人工恢复通道」——被误封的管理员可在此自助解封。
 * 注：路径是 `/security/ip-blacklist`（顶层，不带 /admin 前缀，见 openapi/SecurityController）。
 */
export function listIpBlacklist(): Promise<IpBlacklistEntry[]> {
  if (USE_MOCKS) return mockListIpBlacklist();
  return apiFetch<IpBlacklistEntry[]>('/security/ip-blacklist');
}

/** 人工解封某个 IP（owner）。删除内存镜像 + 持久化表，下次请求即放行。 */
export function unblockIp(ip: string): Promise<{ unblocked: string }> {
  if (USE_MOCKS) return mockUnblockIp(ip);
  return apiFetch<{ unblocked: string }>(`/security/ip-blacklist/${encodeURIComponent(ip)}`, {
    method: 'DELETE',
  });
}


